import { NavLink } from 'react-router'
import { Crown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import { Logo } from '@/components/brand/Logo'
import { PRIMARY_NAV, SETTINGS_NAV, type NavItem } from './nav'

function SideLink({ item }: { item: NavItem }) {
  const { t } = useTranslation()
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      title={t(`nav.${item.key}`)}
      className={({ isActive }) =>
        clsx(
          'group relative flex h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium transition',
          'justify-center lg:justify-start',
          isActive
            ? 'bg-gradient-to-l from-transparent to-gold-400/12 text-gold-text rtl:bg-gradient-to-r'
            : 'text-muted hover:bg-surface-2 hover:text-fg',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute inset-y-2 start-0 w-[3px] rounded-full bg-gold-400" aria-hidden />
          )}
          <Icon className="size-[1.2rem] shrink-0" strokeWidth={isActive ? 2.2 : 1.8} />
          <span className="hidden lg:inline">{t(`nav.${item.key}`)}</span>
        </>
      )}
    </NavLink>
  )
}

export function Sidebar() {
  const { t } = useTranslation()
  return (
    <aside
      className="fixed inset-y-0 start-0 z-30 hidden w-[76px] flex-col border-e border-line bg-surface-0/85 px-3 py-5 backdrop-blur-xl md:flex lg:w-[264px] lg:px-4"
      aria-label={t('nav.menu')}
    >
      <NavLink to="/" className="mb-8 flex justify-center px-1 lg:justify-start" aria-label="GlobalX">
        <span className="hidden lg:block">
          <Logo />
        </span>
        <span className="lg:hidden">
          <Logo compact />
        </span>
      </NavLink>

      <nav className="flex flex-col gap-1">
        {PRIMARY_NAV.map((item) => (
          <SideLink key={item.to} item={item} />
        ))}
      </nav>

      <div className="mt-auto flex flex-col gap-3">
        <SideLink item={SETTINGS_NAV} />
        <NavLink
          to="/pro"
          className="group relative hidden overflow-hidden rounded-2xl border border-gold-400/25 bg-gradient-to-br from-gold-400/12 via-transparent to-blue-500/10 p-4 lg:block"
        >
          <div className="flex items-center gap-2">
            <Crown className="size-5 text-gold-text" />
            <span className="font-semibold">{t('nav.pro')}</span>
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-muted">{t('home.proText')}</p>
        </NavLink>
        <NavLink
          to="/pro"
          title={t('nav.pro')}
          className="flex h-11 items-center justify-center rounded-xl text-gold-text hover:bg-surface-2 lg:hidden"
        >
          <Crown className="size-5" />
        </NavLink>
      </div>
    </aside>
  )
}
